// src/scenes/zbalance/CreateZBalance.jsx
import { useState, useEffect } from "react";
import * as yup from "yup";
import axios from "axios";
import Header from "../../components/Header";
import SimpleForm from "../../components/SimpleForm";
import { Snackbar, Alert } from "@mui/material";

/* ==================== API ==================== */
const api = axios.create({
  baseURL: process.env.REACT_APP_API_URL,
  timeout: 15000,
});

/* ✅ LOAD ONLY SALES USERS */
async function getSalesUsers() {
  const res = await api.get("/api/user", { params: { _t: Date.now() } });
  return res.data.filter(
    (u) => (u.Role?.name || "").toLowerCase().includes("sales")
  );
}

async function postJSON(path, data) {
  try {
    const res = await api.post(path, data);
    return { data: res.data, error: null };
  } catch (err) {
    const serverErrors =
      err?.response?.data?.errors ||
      err?.response?.data?.message ||
      err.message;
    return { data: null, error: serverErrors };
  }
}

/* ==================== HELPERS ==================== */
const buildInitials = (fields) =>
  fields.reduce((acc, f) => {
    const def =
      f.default ??
      (f.type === "checkbox" ? false : f.type === "number" ? "" : "");
    acc[f.name] = def;
    return acc;
  }, {});

const buildSchema = (fields) => {
  const shape = {};
  fields.forEach((f) => {
    let rule;
    switch (f.type) {
      case "email":
        rule = yup.string().email("invalid email");
        break;
      case "number":
        rule = yup.number().typeError("must be a number");
        break;
      case "checkbox":
        rule = yup.boolean();
        break;
      case "date":
        rule = yup.date().typeError("invalid date");
        break;
      default:
        rule = yup.string();
    }
    if (f.required) rule = rule.required("required");
    if (f.min != null) rule = rule.min(f.min);
    if (f.max != null) rule = rule.max(f.max);
    if (f.matches) rule = rule.matches(f.matches.regex, f.matches.message);
    shape[f.name] = rule;
  });
  return yup.object().shape(shape);
};

/* ==================== PAGE ==================== */
export default function CreateZBalance() {
  const [toast, setToast] = useState({ open: false, msg: "", sev: "success" });
  const [salesUsers, setSalesUsers] = useState([]);
  const [isSales, setIsSales] = useState(false);
  const [userId, setUserId] = useState(null);

  /* ✅ CHECK USER ROLE ON EVERY RENDER */
  useEffect(() => {
    const loggedUser = JSON.parse(localStorage.getItem("user")) || {};
    const roleName = loggedUser?.roleName || "";
    setIsSales(roleName.toLowerCase().includes("sales"));
    setUserId(loggedUser?.id || null);
  }, []);

  /* ✅ LOAD ONLY SALES USERS (FOR NON-SALES USERS ONLY) */
  useEffect(() => {
    const fetchSalesUsers = async () => {
      if (!isSales) {
        try {
          const users = await getSalesUsers();
          setSalesUsers(users);
        } catch (error) {
          setToast({ open: true, msg: "Failed to load sales users", sev: "error" });
        }
      }
    };
    
    fetchSalesUsers();
  }, [isSales]); // Re-run when isSales changes

  /* ==================== ROLE-BASED FIELDS ==================== */
  const FIELDS = [
    // ✅ USER FIELD (ADMIN ONLY — SALES USERS ONLY)
    ...(!isSales
      ? [
          {
            name: "userId",
            label: "Sales User",
            type: "select",
            span: 2,
            required: true,
            options: salesUsers.map((u) => ({
              label: u.fullName || u.username,
              value: u.id,
            })),
          },
        ]
      : []),

    {
      name: "zamount",
      label: "Z Amount",
      type: "number",
      span: 2,
      required: true,
     props: {
    placeholder: "Enter amount",
    inputProps: { step: 0.01, min: 0 },
    onKeyDown: (e) => {
      if (e.key === "-" || e.key === "e") {
        e.preventDefault();
      }
    }
  }
    },
    {
      name: "date",
      label: "Date",
      type: "date",
      span: 2,
      required: true,
      props: { InputLabelProps: { shrink: true } },
    },
    {
      name: "bankdeposit",
      label: "Bank Deposit",
      type: "number",
      span: 2,
      required: true,
    props: {
    placeholder: "Enter amount",
    inputProps: { step: 0.01, min: 0 },
    onKeyDown: (e) => {
      if (e.key === "-" || e.key === "e") {
        e.preventDefault();
      }
    }
  }
    },
    {
      name: "totalsales",
      label: "Total Sales",
      type: "number",
      span: 2,
      required: true,
    props: {
    placeholder: "Enter amount",
    inputProps: { step: 0.01, min: 0 },
    onKeyDown: (e) => {
      if (e.key === "-" || e.key === "e") {
        e.preventDefault();
      }
    }
  }
    },
  ];

  /* ==================== INITIAL VALUES ==================== */
  const INITIALS = {
    ...buildInitials(FIELDS),
    userId: isSales ? userId : "",
  };

  /* ==================== VALIDATION ==================== */
  const BASE_SCHEMA = buildSchema(FIELDS);

  const SCHEMA = BASE_SCHEMA.shape({
    userId: isSales
      ? yup.number()
      : yup.number().required("User is required"),
  });

  /* ==================== SUBMIT HANDLER ==================== */
  const handleSubmit = async (values, { setSubmitting, setErrors, resetForm }) => {
    const payload = {
      ...values,
      // ✅ FORCE CORRECT USER BASED ON ROLE
      userId: isSales ? String(userId) : String(values.userId),
    };

    const { data, error } = await postJSON("/api/zbalance", payload);
    setSubmitting(false);

    if (error) {
      if (typeof error === "object" && !Array.isArray(error)) setErrors(error);
      setToast({ open: true, msg: "Failed to create Z Balance", sev: "error" });
      return;
    }

    const msg = data?.message || "Z Balance created successfully";
    setToast({ open: true, msg, sev: "success" });
    resetForm();
  };

  return (
    <>
      <SimpleForm
        title="CREATE Z BALANCE"
        subtitle="Add a new Z Balance entry"
        Header={Header}
        fields={FIELDS}
        initialValues={INITIALS}
        validation={SCHEMA}
        onSubmit={handleSubmit}
        submitLabel="Create Z Balance"
        columns={4}
      />

      <Snackbar
        anchorOrigin={{ vertical: 'top', horizontal: 'right'}}
        sx={{ mt: 5 }}
        open={toast.open}
        autoHideDuration={3000}
        onClose={() => setToast((s) => ({ ...s, open: false }))}
      >
        <Alert
          onClose={() => setToast((s) => ({ ...s, open: false }))}
          severity={toast.sev}
          variant="filled"
        >
          {toast.msg}
        </Alert>
      </Snackbar>
    </>
  );
}